/* PublicHomeMap — Mapa embutido na home pública.
 * Versão compacta do PublicMapLeaflet (com painel lateral, sem fullscreen no preview).
 *
 * Reuso do componente PublicMapLeaflet — apenas wrapper que busca dados públicos
 * e configura o variant "home".
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Globe2, ArrowRight, Radio } from "lucide-react";
import DataSourceBadge from "@/components/DataSourceBadge";
import PublicMapLeaflet from "@/components/PublicMapLeaflet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const fmtNum = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

export default function PublicHomeMap() {
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    axios.get(`${API}/map`).then((r) => setPoints(r.data || [])).catch(() => {});
    axios.get(`${API}/metadata`).then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  const totalDocs = points.reduce((acc, p) => acc + (p.total_documents || 0), 0);

  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-slate-200/80 bg-white shadow-[0_20px_60px_-25px_rgba(10,35,66,0.35)]"
      data-testid="public-home-map"
    >
      {/* HEADER da seção do mapa */}
      <div className="px-6 md:px-8 pt-6 pb-5 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap bg-gradient-to-b from-white to-slate-50/40">
        <div>
          <div className="flex items-center gap-2.5 mb-2 flex-wrap">
            <DataSourceBadge
              mode={meta?.data_source_mode}
              shortLabel={meta?.data_source_short_label}
              note={meta?.methodology_note}
            />
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-amber-700">
              <Radio size={11} /> {fmtNum(points.length)} focos · {fmtNum(totalDocs)} documentos
            </span>
          </div>
          <h3 className="font-display font-black text-2xl md:text-[1.7rem] text-[#0a2342] tracking-tight leading-tight">
            Mapa Global de Produção Prospectiva
          </h3>
          <p className="text-[13px] text-slate-500 mt-1.5 max-w-xl leading-relaxed">
            Distribuição geográfica de publicações, documentos e pesquisas monitoradas pelo observatório.
          </p>
        </div>

        <Link
          to="/mapa-publico"
          className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide px-4 py-2.5 bg-[#0a2342] text-white rounded-md hover:bg-[#142e4f] transition shadow-sm border border-amber-400/30"
          data-testid="cta-open-full-map"
        >
          <Globe2 size={13} /> Abrir mapa completo <ArrowRight size={12} />
        </Link>
      </div>

      {/* Mapa interativo Leaflet */}
      <PublicMapLeaflet
        points={points}
        meta={meta}
        height={560}
        variant="home"
        theme="dark"
        showSidePanel
        showThemeToggle
        showFullscreen
      />
    </div>
  );
}
